

  
const content = `# Database Encryption Rules

## Objective

***
The Data Encryption services provide a uniform approach for encrypting sensitive data in various types of databases, such as Microsoft SQL, Oracle, PostgreSQL, MySQL, and MariaDB.
The objective of the document is to provide details on the OnData encryption rules so that OnData users can select the appropriate encryption rule for each data field.
Also, the limitations for the Data Encryption services are listed as well.
***

## Document information

***
Document Version: 1.2  
Published Date: 07/12/2023  
  
***

## Encryption Rules

***
To simplify the data encryption process, OnData offers a set of straightforward encryption rules that can be easily applied to encrypt the sensitive data in any database.
Below is the list of encryption rules and when to use them.

* Skip - This rule indicates that no encryption is necessary for a data field. **NOTE:** Only data fields that are sensitive in nature should be encrypted. For any data
fields that are not sensitive in nature, such as database IDs and rowguid, please use the "Skip" rule. Also, you may have to use the "Skip" rule to handle the
encryption limitations as detailed in the "OnData Encryption Limitations" section below.
* Address - This rule is used to encrypt the street address part of a full address or a full address.
* Alphanumeric Text - This rule is used to encrypt a string with alphanumeric characters.
* City - This rule is used to encrypt the name of a city or the name of a state.
* Credit/Debit Card - This rule is used to encrypt any credit card numbers and debit card numbers.
* Date - This rule is used to encrypt any dates, such as birth date, hire date or any other datetime fields. For datetime fields, only the date part will be encrypted. The
time part of the datetime will be preserved without encryption.
* Decimal - This rule is used to encrypt any decimal numbers.
* Email Address - This rule is used to encrypt an email address, with the top level domain preserved.
* Hexadecimal Number - This rule is used to encrypt any hexadecimal numbers.
* Integer - This rule is used to encrypt any integer numbers. **NOTE:** Only whole numbers can be encrypted with this encryption rule.
* IPv4 Address - This rule is used to encrypt any IPv4 addresses.
* IPv6 Address - This rule is used to encrypt any IPv6 addresses.
* Multi Language - This rule is used to encrypt strings that contains language characters beyond English characters, such as Spanish, Chinese, and emoji characters.
* Name - This rule is used to encrypt any name-related fields, such as a person's first name, last name, contact name, organization name, and product name.
* Personal ID Number - This rule is used to encrypt an ID number for a person, such as National ID, Social Security Number, Driver's License Number, or any other ID.
* Phone/Fax - This rule is used to encrypt a phone number, fax number, with or without country code and/or area code.
* Postal Code - This rule is used encrypt a zip code or postal code.
* Regular Text - This rule is used to encrypt any characters available on a US English keyboard.
* Serial Number - This rule is used to encrypt any serial number, such as product serial number and model number.
* Title - This rule is used to encrypt title-related fields, such as a person's title/role, and a book title.
* Validated Card Number - This rule is used to encrypt any credit card numbers and debit card numbers, but preserving the issuing bank information and passing the Luhn validation as well.
* Validated SSN - This rule is used to encrypt U.S. Social Security Numbers. The encrypted value will pass the SSN validation.
* XML - This rule is used to encrypt XML data types that are supported by various database engines.
* Year Preserved Date - This rule is used to encrypt any dates and retain the original year, this will allow me to encrypt birthdates and still understand the year which may be important to some data processing tasks. As an example: 1999-12-05 could result in 1999-04-03. (year is preserved)

For each column in a database table, the data type of that column will determine the list of available encryption rules that could be used for encrypting that column. Users can
select the most appropriate encryption rule to encrypt the data that should be protected.

The OnData encryption will preserve the data type and format, as well as the length of the data in most cases.
`
  
export default content;
